<template>
  <div class="mobile-questionnaire-preview-page">
    <div class="questionnaire-content" v-if="questionnaireTemplate">
      <div class="questionnaire-header">
        <h2>问卷预览</h2>
        
        <div class="preview-notice">
          <span class="notice-icon">ℹ️</span>
          <span class="notice-text">这是问卷预览模式，您可以查看所有题目但无法提交答案</span>
        </div>
        
        <div class="questionnaire-intro">
          <p>您好！</p>
          <p>我们邀请您参加一项职业健康状况问卷调查，本问卷调查旨在了解您的职业心理、身体活动水平、肌肉骨骼疾患的基本情况，为职业人群的健康保护提供依据。我们保证对您提供的个人信息资料进行保密，并承诺所有资料仅用于学术研究。谢谢您的合作！</p>
          <p>广东省职业健康协会</p>
        </div>
      </div>
      
      <div class="questionnaire-questions">
        <template v-for="question in visibleQuestions" :key="question.id">
          <!-- Likert复合题 -->
          <LikertCompositeQuestion
            v-if="question.type === 'LIKERT_COMPOSITE'"
            :ref="el => setQuestionRef(question.id, el)"
            :question-id="question.id"
            :display-index="question.displayIndex"
            :title="question.title"
            :required="question.required"
            :has-error="false"
            :likert-config="question.compositeConfig.likert.scaleConfig"
            :sub-questions="question.compositeConfig.likert.subQuestions"
            :model-value="answers"
            @update:modelValue="handleAnswersUpdate"
          />
          
          <!-- 其他题型 -->
          <QuestionnaireItem
            v-else
            :ref="el => setQuestionRef(question.id, el)"
            :question="question"
            :current-index="question.displayIndex"
            :total-count="viewQuestions.length"
            :show-progress="true"
            :all-answers="answers"
            :has-error="false"
            v-model="answers[question.id]"
          />
        </template>
      </div>
      
      <div class="preview-footer">
        <p>— 问卷预览结束 —</p>
        <p class="template-info">模板代码: {{ templateCode }}</p>
      </div>
    </div>
    
    <div v-if="!questionnaireTemplate && !error" class="loading-container">
      <van-loading type="spinner" vertical>加载问卷模板中...</van-loading>
    </div>
    
    <div v-if="error" class="error-container">
      <p class="error-text">{{ error }}</p>
      <van-button type="primary" @click="loadQuestionnaireTemplate">重新加载</van-button>
    </div>
  </div>
</template>

<script>
import QuestionnaireItem from "@/components/QuestionnaireItem.vue";
import LikertCompositeQuestion from "@/components/LikertCompositeQuestion.vue";
import { get } from "@/request.js";
import { transformBackendToView, calculateVisibleQuestions } from "@/utils/questionnaireTransformer.js";

export default {
  name: "MobileQuestionnairePreview",
  components: {
    QuestionnaireItem,
    LikertCompositeQuestion
  },
  data() {
    return {
      questionnaireTemplate: null,
      viewQuestions: [],
      answers: {},
      error: null,
      questionRefs: new Map()  // 题目组件的 ref 引用，用于滚动定位
    };
  },
  computed: {
    templateCode() {
      return this.$route.params.templateCode;
    },
    sortedViewQuestions() {
      return [...this.viewQuestions].sort((a, b) => a.displayIndex - b.displayIndex);
    },
    // 根据答案和跳转逻辑计算可见题目列表
    visibleQuestions() {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return [];
      }
      return calculateVisibleQuestions(this.sortedViewQuestions, this.answers);
    }
  },
  watch: {
    answers: {
      handler(newAnswers, oldAnswers) {
        // 检查跳转逻辑，如果触发跳转，滚动到目标题目
        this.$nextTick(() => {
          this.handleSkipLogic(newAnswers, oldAnswers);
        });
      },
      deep: true
    }
  },
  mounted() {
    this.loadQuestionnaireTemplate();
  },
  methods: {
    async loadQuestionnaireTemplate() {
      this.error = null;
      try {
        const res = await get(`/api/public/questionnaire/preview/${this.templateCode}`);
        if (res.success && res.data) {
          this.questionnaireTemplate = res.data;
          this.viewQuestions = transformBackendToView(this.questionnaireTemplate.questions);
          
          // 初始化答案对象（只为了展示，不会提交）
          this.questionnaireTemplate.questions.forEach(question => {
            if (question.type === 'MULTIPLE_CHOICE') {
              this.answers[question.id] = [];
            } else if (question.type === 'LIKERT_SCALE' && question.parentQuestionId) {
              // Likert子题的答案存储在复合题的 answers 对象中
              // 初始化时不需要单独处理，由 LikertCompositeQuestion 组件管理
            } else if (question.type === 'MATRIX') {
              // 矩阵题：答案格式为 {rowId: columnValue}，初始化为空对象
              this.answers[question.id] = {};
            } else if (question.type === 'TEXT') {
              this.answers[question.id] = ''; // TEXT 类型初始化为空字符串
            } else {
              this.answers[question.id] = null;
            }
          });
          
          // 初始化 Likert 复合题的答案对象（空对象）
          this.viewQuestions
            .filter(q => q.type === 'LIKERT_COMPOSITE')
            .forEach(composite => {
              if (!this.answers[composite.id]) {
                this.answers[composite.id] = {};
              }
            });
        } else {
          this.error = res.message || '加载问卷模板失败';
        }
      } catch (err) {
        console.error('加载问卷模板失败:', err);
        this.error = (err && err.message) || '网络错误，请稍后重试';
      }
    },
    handleAnswersUpdate(newAnswers) {
      this.answers = { ...this.answers, ...newAnswers };
    },
    
    // 处理跳转逻辑
    handleSkipLogic(newAnswers, oldAnswers) {
      if (!this.sortedViewQuestions || this.sortedViewQuestions.length === 0) {
        return;
      }
      
      // 找出答案发生变化的题目
      const changedQuestions = this.sortedViewQuestions.filter(q => {
        const newAnswer = newAnswers[q.id];
        const oldAnswer = oldAnswers?.[q.id];
        return newAnswer !== oldAnswer && newAnswer !== null && newAnswer !== undefined;
      });
      
      // 检查每个变化题目的跳转逻辑
      for (const question of changedQuestions) {
        const skipLogic = question.skipLogic;
        if (!skipLogic) continue;
        
        const answer = newAnswers[question.id];
        const answerValue = String(answer);
        let shouldSkip = false;
        
        if (skipLogic.condition === 'EQUALS') {
          shouldSkip = answerValue === skipLogic.conditionValue;
        } else if (skipLogic.condition === 'NOT_EQUALS') {
          shouldSkip = answerValue !== skipLogic.conditionValue;
        }
        
        if (shouldSkip) {
          // 如果跳过的题目有答案，清空它
          if (skipLogic.targetQuestionId) {
            // 跳转到指定题目，清空跳过的题目答案
            const skippedQuestionIds = this.getSkippedQuestionIds(question.id, skipLogic.targetQuestionId);
            skippedQuestionIds.forEach(skippedId => {
              // 清空被跳过题目的答案
              const skippedQuestion = this.sortedViewQuestions.find(q => q.id === skippedId);
              if (skippedQuestion) {
                if (skippedQuestion.type === 'MULTIPLE_CHOICE') {
                  this.answers[skippedId] = [];
                } else if (skippedQuestion.type === 'MATRIX') {
                  this.answers[skippedId] = {};
                } else {
                  this.answers[skippedId] = null;
                }
              }
            });
            
            // 滚动到目标题目
            setTimeout(() => {
              this.scrollToQuestion(skipLogic.targetQuestionId);
            }, 300); // 延迟300ms，等待DOM更新
          } else {
            // 跳过下一题，清空下一题的答案
            const currentIndex = this.sortedViewQuestions.findIndex(q => q.id === question.id);
            if (currentIndex >= 0 && currentIndex + 1 < this.sortedViewQuestions.length) {
              const nextQuestion = this.sortedViewQuestions[currentIndex + 1];
              if (nextQuestion.type === 'MULTIPLE_CHOICE') {
                this.answers[nextQuestion.id] = [];
              } else if (nextQuestion.type === 'MATRIX') {
                this.answers[nextQuestion.id] = {};
              } else {
                this.answers[nextQuestion.id] = null;
              }
            }
          }
        }
      }
    },
    
    // 获取被跳过的题目ID列表（从当前题目到目标题目之间的所有题目）
    getSkippedQuestionIds(currentQuestionId, targetQuestionId) {
      const currentIndex = this.sortedViewQuestions.findIndex(q => q.id === currentQuestionId);
      const targetIndex = this.sortedViewQuestions.findIndex(q => q.id === targetQuestionId);
      
      if (currentIndex < 0 || targetIndex < 0 || targetIndex <= currentIndex) {
        return [];
      }
      
      const skippedIds = [];
      for (let i = currentIndex + 1; i < targetIndex; i++) {
        skippedIds.push(this.sortedViewQuestions[i].id);
      }
      return skippedIds;
    },
    
    // 设置题目组件的 ref
    setQuestionRef(questionId, el) {
      if (el) {
        this.questionRefs.set(questionId, el);
      } else {
        this.questionRefs.delete(questionId);
      }
    },
    
    // 滚动到指定题目
    scrollToQuestion(questionId) {
      this.$nextTick(() => {
        // 方案1：尝试使用 ref
        const questionRef = this.questionRefs.get(questionId);
        if (questionRef && questionRef.$el) {
          const element = questionRef.$el;
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          return;
        }
        
        // 方案2：尝试通过题目序号查找（更可靠）
        const question = this.viewQuestions.find(q => q.id === questionId);
        if (question) {
          // 通过 data-question-id 属性查找
          const questionElement = document.querySelector(`[data-question-id="${questionId}"]`);
          if (questionElement) {
            questionElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            return;
          }
        }
        
        // 方案3：如果都找不到，尝试通过题目序号查找
        console.warn(`无法找到题目 ${questionId} 的DOM元素，无法滚动`);
      });
    }
  }
};
</script>

<style lang="less" scoped>
.mobile-questionnaire-preview-page {
  position: relative;
  min-height: 100vh;
  background-color: #f5f5f5;
  
  .questionnaire-content {
    background-color: #fff;
    
    // PC端：居中卡片式布局（与职业健康调查问卷一致）
    @media (min-width: 768px) {
      width: 60%;
      margin: 10% 20%;
      margin-bottom: 100px;
      border-radius: 4px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
      padding: 30px;
    }
    
    // 移动端：保持全屏
    @media (max-width: 767px) {
      padding-bottom: 20px;
    }
  }
  
  .questionnaire-header {
    padding: 20px 16px;
    margin-bottom: 20px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f0f0f0;
    
    // PC端样式（与职业健康调查问卷一致）
    @media (min-width: 768px) {
      padding: 0;
      margin-bottom: 30px;
      padding-bottom: 20px;
      background: none;
      color: #333;
    }
    
    // 移动端：渐变背景
    @media (max-width: 767px) {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;
      border-bottom: none;
    }
    
    h2 {
      margin: 0 0 16px 0;
      font-size: 24px;
      font-weight: 600;
      text-align: center;
      
      // PC端：蓝色标题
      @media (min-width: 768px) {
        color: #0095ff;
        margin-bottom: 20px;
      }
    }
    
    .preview-notice {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      margin-bottom: 16px;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 8px;
      font-size: 14px;
      
      // PC端样式
      @media (min-width: 768px) {
        background-color: #fff3e0;
        color: #e65100;
        border: 1px solid #ffe0b2;
      }
      
      .notice-icon {
        font-size: 18px;
      }
      
      .notice-text {
        flex: 1;
        line-height: 1.5;
      }
    }
    
    .questionnaire-intro {
      font-size: 14px;
      line-height: 1.8;
      opacity: 0.95;
      
      // PC端样式
      @media (min-width: 768px) {
        opacity: 1;
        color: #333;
        font-size: 15px;
        line-height: 2.2;
        text-align: left;
        margin-top: 16px;
      }
      
      p {
        margin: 0 0 8px 0;
        
        @media (min-width: 768px) {
          margin: 0 0 12px 0;
        }
        
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
  
  .questionnaire-questions {
    background-color: #f5f5f5;
    
    // PC端：添加内边距
    @media (min-width: 768px) {
      padding: 0 40px;
      background-color: #fff;
    }
  }
  
  .preview-footer {
    padding: 40px 16px 20px;
    text-align: center;
    color: #999;
    background-color: #fff;
    
    // PC端：增加内边距
    @media (min-width: 768px) {
      padding: 40px 40px 40px;
    }
    
    p {
      margin: 8px 0;
      font-size: 14px;
    }
    
    .template-info {
      font-size: 12px;
      color: #ccc;
    }
  }
  
  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 20px;
    
    .error-text {
      margin-bottom: 20px;
      color: #ff4d4f;
      font-size: 16px;
      text-align: center;
    }
  }
}
</style>

